class CharmBar {
  constructor(el) {
    this.el = el;
    this.grid = el.querySelector('.charm-bar__grid');
    this.footer = el.querySelector('.charm-bar__footer');
    this.countEl = el.querySelector('.charm-bar__selected-count');
    this.totalEl = el.querySelector('.charm-bar__selected-total');
    this.addBtn = el.querySelector('.charm-bar__add-btn');
    this.collectionHandle = this.grid?.dataset.collection;
    this.moneyFormat = el.dataset.moneyFormat || '${{amount}}';

    // Keyed by String(product.id) — one entry per charm product, not per variant
    this.selectedCharms = new Map();
    this.products = [];
    this.panel = null;
    this._keyHandler = null;

    if (this.collectionHandle) this.loadCharms();
    this.addBtn?.addEventListener('click', () => this.addToCart());
  }

  // ── Data loading ─────────────────────────────────────────────────────────────

  async loadCharms() {
    try {
      const res = await fetch(`/collections/${this.collectionHandle}/products.json?limit=250`);
      if (!res.ok) throw new Error('fetch failed');
      const { products } = await res.json();
      this.products = products;
      this.renderCharms(products);
    } catch {
      this.grid.innerHTML = '<p class="charm-bar__empty">Charms could not be loaded. Please refresh the page.</p>';
    }
  }

  // ── Card rendering ────────────────────────────────────────────────────────────

  renderCharms(products) {
    if (!products.length) {
      this.grid.innerHTML = '<p class="charm-bar__empty">No charms available yet — check back soon.</p>';
      return;
    }

    this.grid.innerHTML = products.map(p => this.buildCard(p)).join('');

    this.grid.querySelectorAll('.charm-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const product = this.products.find(p => String(p.id) === btn.dataset.productId);
        if (!product) return;

        if (product.variants.length === 1) {
          this.toggleSingle(btn, product);
        } else {
          this.openPanel(product);
        }
      });
    });
  }

  buildCard(product) {
    const isMulti = product.variants.length > 1;
    const minPrice = Math.min(...product.variants.map(v => v.price));
    const img = product.images[0];
    const imgSrc = img ? img.src.replace(/\?.*$/, '') + '?width=220' : '';
    const priceLabel = isMulti
      ? `from ${this.formatMoney(minPrice)}`
      : `+ ${this.formatMoney(product.variants[0].price)}`;

    return `
      <button
        class="charm-item${isMulti ? ' charm-item--multi' : ''}"
        type="button"
        data-product-id="${product.id}"
        aria-pressed="false"
        aria-label="${this.esc(product.title)}, ${priceLabel}"
      >
        <div class="charm-item__image-wrap">
          ${imgSrc
            ? `<img class="charm-item__img" src="${imgSrc}" alt="${this.esc(product.title)}" width="110" height="110" loading="lazy">`
            : `<span class="charm-item__dot-fallback"></span>`
          }
          <span class="charm-item__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>
          ${isMulti ? `<span class="charm-item__options-badge">${product.variants.length} options</span>` : ''}
        </div>
        <span class="charm-item__name">${this.esc(product.title)}</span>
        <span class="charm-item__variant-label"></span>
        <span class="charm-item__price">${priceLabel}</span>
      </button>`;
  }

  // ── Single-variant toggle ─────────────────────────────────────────────────────

  toggleSingle(btn, product) {
    const productId = String(product.id);
    const variant = product.variants[0];

    if (this.selectedCharms.has(productId)) {
      this.selectedCharms.delete(productId);
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('charm-item--selected');
    } else {
      this.selectedCharms.set(productId, {
        productId,
        variantId: String(variant.id),
        title: product.title,
        variantTitle: '',
        price: variant.price,
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('charm-item--selected');
    }

    this.updateFooter();
  }

  // ── Variant panel ─────────────────────────────────────────────────────────────

  openPanel(product) {
    this.closePanel(false);

    const existing = this.selectedCharms.get(String(product.id));
    const activeVariant = existing
      ? product.variants.find(v => String(v.id) === existing.variantId) || product.variants[0]
      : product.variants[0];

    const panel = document.createElement('div');
    panel.className = 'charm-variant-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', `Choose ${this.esc(product.title)} option`);
    panel.innerHTML = this.buildPanelHTML(product, activeVariant, !!existing);
    document.body.appendChild(panel);
    this.panel = panel;

    requestAnimationFrame(() => panel.classList.add('charm-variant-panel--open'));

    // Backdrop
    panel.querySelector('.charm-variant-panel__backdrop')
      .addEventListener('click', () => this.closePanel());

    // Close button
    panel.querySelector('.charm-variant-panel__close')
      .addEventListener('click', () => this.closePanel());

    // Remove button (only present when already selected)
    panel.querySelector('.charm-variant-panel__remove')
      ?.addEventListener('click', () => {
        this.deselectProduct(String(product.id));
        this.closePanel();
      });

    // Variant buttons
    panel.querySelectorAll('.charm-variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        panel.querySelectorAll('.charm-variant-btn')
          .forEach(b => b.classList.remove('charm-variant-btn--active'));
        btn.classList.add('charm-variant-btn--active');

        // Update large image
        const imgSrc = btn.dataset.image;
        if (imgSrc) {
          const panelImg = panel.querySelector('.charm-variant-panel__img');
          panelImg.style.opacity = '0';
          setTimeout(() => {
            panelImg.src = imgSrc.replace(/\?.*$/, '') + '?width=500';
            panelImg.style.opacity = '1';
          }, 120);
        }

        // Update displayed price
        const variant = product.variants.find(v => String(v.id) === btn.dataset.variantId);
        if (variant) {
          panel.querySelector('.charm-variant-panel__price').textContent
            = `+ ${this.formatMoney(variant.price)}`;
        }
      });
    });

    // Confirm button
    panel.querySelector('.charm-variant-panel__add').addEventListener('click', () => {
      const activeBtn = panel.querySelector('.charm-variant-btn--active');
      if (!activeBtn) return;
      const variant = product.variants.find(v => String(v.id) === activeBtn.dataset.variantId);
      if (!variant) return;

      this.confirmVariant(product, variant, activeBtn.dataset.image || '');
      this.closePanel();
    });

    this._keyHandler = e => { if (e.key === 'Escape') this.closePanel(); };
    document.addEventListener('keydown', this._keyHandler);
  }

  buildPanelHTML(product, activeVariant, isAlreadySelected) {
    const activeImg = this.getVariantImage(product, activeVariant);
    const imgSrc = activeImg ? activeImg.replace(/\?.*$/, '') + '?width=500' : '';

    // Use image swatches when variants have dedicated images (e.g. scarves)
    const useImgSwatches = product.variants.length > 5 &&
      product.variants.every(v => v.featured_image?.src || product.images.some(i => i.variant_ids?.includes(v.id)));

    const variantBtns = product.variants.map(v => {
      const vImg = this.getVariantImage(product, v);
      const isActive = v.id === activeVariant.id;
      const label = v.title === 'Default Title' ? product.title : v.title;

      if (useImgSwatches && vImg) {
        const imgSrc = vImg.replace(/\?.*$/, '') + '?width=100';
        return `
          <button class="charm-variant-btn charm-variant-btn--swatch${isActive ? ' charm-variant-btn--active' : ''}"
                  type="button"
                  data-variant-id="${v.id}"
                  data-image="${this.esc(vImg)}"
                  title="${this.esc(label)}">
            <img src="${this.esc(imgSrc)}" alt="${this.esc(label)}" loading="lazy">
            <span class="charm-variant-swatch-num">${this.esc(label.replace('Style ', ''))}</span>
          </button>`;
      }

      return `
        <button class="charm-variant-btn${isActive ? ' charm-variant-btn--active' : ''}"
                type="button"
                data-variant-id="${v.id}"
                data-image="${this.esc(vImg || '')}">
          ${this.esc(label)}
        </button>`;
    }).join('');

    return `
      <div class="charm-variant-panel__backdrop"></div>
      <div class="charm-variant-panel__sheet">
        <button class="charm-variant-panel__close" type="button" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div class="charm-variant-panel__content">

          <div class="charm-variant-panel__image-area">
            <img class="charm-variant-panel__img"
                 src="${imgSrc}"
                 alt="${this.esc(product.title)}"
                 width="250" height="250">
          </div>

          <div class="charm-variant-panel__detail">
            <h3 class="charm-variant-panel__title">${this.esc(product.title)}</h3>
            <p class="charm-variant-panel__price">+ ${this.formatMoney(activeVariant.price)}</p>
            <p class="charm-variant-panel__choose-label">Choose your option:</p>
            <div class="charm-variant-panel__variants">${variantBtns}</div>
            <button class="charm-variant-panel__add" type="button">
              ${isAlreadySelected ? 'Update Charm' : 'Add this Charm'}
            </button>
            ${isAlreadySelected
              ? `<button class="charm-variant-panel__remove" type="button">Remove charm</button>`
              : ''}
          </div>

        </div>
      </div>`;
  }

  getVariantImage(product, variant) {
    // Use variant's dedicated image if set
    if (variant.featured_image?.src) return variant.featured_image.src;
    // Fall back to any image tagged to this variant
    const tagged = product.images.find(
      img => Array.isArray(img.variant_ids) && img.variant_ids.includes(variant.id)
    );
    if (tagged) return tagged.src;
    // Fall back to product's first image
    return product.images[0]?.src || '';
  }

  closePanel(animate = true) {
    if (!this.panel) return;
    const panel = this.panel;
    this.panel = null;

    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }

    if (animate) {
      panel.classList.remove('charm-variant-panel--open');
      setTimeout(() => panel.remove(), 350);
    } else {
      panel.remove();
    }
  }

  // ── Selection management ──────────────────────────────────────────────────────

  confirmVariant(product, variant, imageSrc) {
    const productId = String(product.id);

    this.selectedCharms.set(productId, {
      productId,
      variantId: String(variant.id),
      title: product.title,
      variantTitle: variant.title !== 'Default Title' ? variant.title : '',
      price: variant.price,
    });

    this.updateCard(productId, variant, imageSrc);
    this.updateFooter();
  }

  deselectProduct(productId) {
    this.selectedCharms.delete(productId);
    const card = this.grid.querySelector(`[data-product-id="${productId}"]`);
    if (!card) return;

    card.classList.remove('charm-item--selected');
    card.setAttribute('aria-pressed', 'false');
    card.querySelector('.charm-item__variant-label').textContent = '';

    // Reset price to "from …" for multi-variant or original price for single
    const product = this.products.find(p => String(p.id) === productId);
    if (product) {
      const isMulti = product.variants.length > 1;
      const minPrice = Math.min(...product.variants.map(v => v.price));
      card.querySelector('.charm-item__price').textContent = isMulti
        ? `from ${this.formatMoney(minPrice)}`
        : `+ ${this.formatMoney(product.variants[0].price)}`;
    }

    this.updateFooter();
  }

  updateCard(productId, variant, imageSrc) {
    const card = this.grid.querySelector(`[data-product-id="${productId}"]`);
    if (!card) return;

    card.classList.add('charm-item--selected');
    card.setAttribute('aria-pressed', 'true');

    if (imageSrc) {
      const img = card.querySelector('.charm-item__img');
      if (img) img.src = imageSrc.replace(/\?.*$/, '') + '?width=220';
    }

    const labelEl = card.querySelector('.charm-item__variant-label');
    if (labelEl) {
      labelEl.textContent = variant.title !== 'Default Title' ? variant.title : '';
    }

    card.querySelector('.charm-item__price').textContent
      = `+ ${this.formatMoney(variant.price)}`;
  }

  // ── Footer ────────────────────────────────────────────────────────────────────

  updateFooter() {
    const count = this.selectedCharms.size;
    const total = [...this.selectedCharms.values()].reduce((s, c) => s + parseFloat(c.price), 0);
    this.footer.hidden = count === 0;
    this.countEl.textContent = `${count} charm${count !== 1 ? 's' : ''} selected`;
    this.totalEl.textContent = `+ ${this.formatMoney(total)}`;
  }

  // ── Add to cart ───────────────────────────────────────────────────────────────

  async addToCart() {
    if (!this.selectedCharms.size || this.addBtn.disabled) return;

    this.addBtn.disabled = true;
    const originalText = this.addBtn.textContent;
    this.addBtn.textContent = 'Adding…';

    const items = [...this.selectedCharms.values()].map(c => ({
      id: parseInt(c.variantId, 10),
      quantity: 1,
      properties: {
        _from_charm_bar: 'true',
        ...(c.variantTitle ? { Option: c.variantTitle } : {}),
      },
    }));

    try {
      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add charms');
      }

      this.clearSelections();
      this.refreshCartUI();

      this.addBtn.textContent = '✓ Charms Added!';
      this.addBtn.classList.add('charm-bar__add-btn--success');
      setTimeout(() => {
        this.addBtn.textContent = originalText;
        this.addBtn.classList.remove('charm-bar__add-btn--success');
        this.addBtn.disabled = false;
      }, 2200);
    } catch (err) {
      this.addBtn.textContent = err.message || 'Error — please try again';
      setTimeout(() => {
        this.addBtn.textContent = originalText;
        this.addBtn.disabled = false;
      }, 2500);
    }
  }

  clearSelections() {
    const productIds = [...this.selectedCharms.keys()];
    this.selectedCharms.clear();
    productIds.forEach(id => {
      const card = this.grid.querySelector(`[data-product-id="${id}"]`);
      if (!card) return;
      card.classList.remove('charm-item--selected');
      card.setAttribute('aria-pressed', 'false');
      card.querySelector('.charm-item__variant-label').textContent = '';

      const product = this.products.find(p => String(p.id) === id);
      if (product) {
        const isMulti = product.variants.length > 1;
        const minPrice = Math.min(...product.variants.map(v => v.price));
        const img = product.images[0];
        const imgEl = card.querySelector('.charm-item__img');
        if (imgEl && img) imgEl.src = img.src.replace(/\?.*$/, '') + '?width=220';
        card.querySelector('.charm-item__price').textContent = isMulti
          ? `from ${this.formatMoney(minPrice)}`
          : `+ ${this.formatMoney(product.variants[0].price)}`;
      }
    });
    this.updateFooter();
  }

  refreshCartUI() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          el.classList.remove('hidden');
          const n = el.querySelector('[aria-hidden]');
          if (n) n.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (typeof window.publish === 'function') {
          window.publish('cart:refresh', { source: 'charm-bar' });
        }
      })
      .catch(() => {});
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  formatMoney(price) {
    const num = parseFloat(price);
    const amount = num.toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(num))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

document.querySelectorAll('.charm-bar').forEach(el => new CharmBar(el));
